import clsx from 'clsx';

import { Button } from '../button/Button';
import { ProgressBar } from '../progress/ProgressBar';

import styles from './Upload.module.css';

import type { SourceDocument } from '../../../domain/analysis/analysis';

const TONE: Record<SourceDocument['status'], string> = {
  pending: 'var(--chart-idle)',
  uploading: 'var(--chart-info)',
  uploaded: 'var(--chart-success)',
  failed: 'var(--chart-danger)',
};

function statusText(doc: SourceDocument): string {
  switch (doc.status) {
    case 'uploading':
      return `Uploading… ${doc.progress}%`;
    case 'uploaded':
      return 'Uploaded';
    case 'failed':
      return doc.error ?? 'Upload failed';
    case 'pending':
      return 'Queued';
  }
}

export interface UploadListProps {
  readonly documents: readonly SourceDocument[];
  readonly onRetry?: (id: string) => void;
  readonly onRemove?: (id: string) => void;
}

export function UploadList({ documents, onRetry, onRemove }: UploadListProps) {
  if (documents.length === 0) {
    return <p className={styles.empty}>No files uploaded yet.</p>;
  }

  return (
    <ul className={styles.list}>
      {documents.map((doc) => (
        <li key={doc.id} className={styles.row}>
          <span className={styles.icon} aria-hidden="true">
            📄
          </span>
          <div className={styles.info}>
            <p className={styles.name}>{doc.fileName}</p>
            <p className={clsx(styles.status, doc.status === 'failed' && styles.failed)}>
              {statusText(doc)}
            </p>
            <ProgressBar
              value={doc.progress}
              color={TONE[doc.status]}
              label={`${doc.fileName} upload progress`}
            />
          </div>
          {doc.status === 'failed' && onRetry && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onRetry(doc.id);
              }}
            >
              Retry
            </Button>
          )}
          {onRemove && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                onRemove(doc.id);
              }}
              aria-label={`Remove ${doc.fileName}`}
            >
              ✕
            </Button>
          )}
        </li>
      ))}
    </ul>
  );
}
